import React, { useId } from 'react';

/**
 * Premium badge icons — each is a self-contained SVG combining a gold
 * 8-pointed rosette frame + colored field + central pictorial emblem.
 *
 * Note: we deliberately do NOT use a shared wrapper component with `children`
 * because the framework injects tracking `<span>` wrappers around JSX children
 * which breaks SVG rendering. Every icon inlines the rosette markup below.
 */

/* SVG snippet that renders the shared frame — used via string concat inside each icon. */
const useRosetteIds = () => {
  const raw = useId().replace(/[^a-zA-Z0-9]/g, '');
  return { field: `fieldG_${raw}`, gold: `goldG_${raw}` };
};

const FrameDefs = ({ fieldId, goldId, fieldColor }) => (
  <defs>
    <radialGradient id={fieldId} cx="50%" cy="45%" r="60%">
      <stop offset="0%" stopColor={fieldColor} stopOpacity="0.95" />
      <stop offset="100%" stopColor={fieldColor} stopOpacity="1" />
    </radialGradient>
    <linearGradient id={goldId} x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stopColor="#d9b46a" />
      <stop offset="50%" stopColor="#f3d896" />
      <stop offset="100%" stopColor="#a17a42" />
    </linearGradient>
  </defs>
);

const FrameStar = ({ goldId }) => (
  <g fill={`url(#${goldId})`}>
    <path d="M 0 -56 L 12 -18 L 50 -30 L 24 0 L 50 30 L 12 18 L 0 56 L -12 18 L -50 30 L -24 0 L -50 -30 L -12 -18 Z" />
    <path d="M 0 -56 L 12 -18 L 50 -30 L 24 0 L 50 30 L 12 18 L 0 56 L -12 18 L -50 30 L -24 0 L -50 -30 L -12 -18 Z" transform="rotate(22.5)" opacity="0.55" />
  </g>
);

const FrameField = ({ fieldId, ring, accent }) => (
  <g>
    <circle r="36" fill={`url(#${fieldId})`} />
    <circle r="36" fill="none" stroke={ring} strokeWidth="1.4" opacity="0.85" />
    <circle r="33" fill="none" stroke={accent} strokeWidth="0.5" opacity="0.6" />
    <circle r="1" cx="40" cy="0" fill={accent} />
    <circle r="1" cx="34.64" cy="20" fill={accent} />
    <circle r="1" cx="20" cy="34.64" fill={accent} />
    <circle r="1" cx="0" cy="40" fill={accent} />
    <circle r="1" cx="-20" cy="34.64" fill={accent} />
    <circle r="1" cx="-34.64" cy="20" fill={accent} />
    <circle r="1" cx="-40" cy="0" fill={accent} />
    <circle r="1" cx="-34.64" cy="-20" fill={accent} />
    <circle r="1" cx="-20" cy="-34.64" fill={accent} />
    <circle r="1" cx="0" cy="-40" fill={accent} />
    <circle r="1" cx="20" cy="-34.64" fill={accent} />
    <circle r="1" cx="34.64" cy="-20" fill={accent} />
  </g>
);

// ------- ICONS -------
// Each icon composes: FrameDefs -> outer <g translate(60 60)> containing FrameStar, FrameField, and its pictorial. All INLINE, no children prop.

export const IconSteadfast = () => {
  const { field, gold } = useRosetteIds();
  const FIELD = '#2f5241';
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor={FIELD} />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#c8a25c" accent="#e8c98b" />
        <g fill="#e8c98b" stroke="#e8c98b" strokeWidth="0.6">
          <g transform="translate(0 -12)">
            <path d="M 0 0 Q -14 -6 -20 -14" fill="none" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M 0 0 Q -8 -10 -8 -20" fill="none" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M 0 0 Q 0 -14 4 -22" fill="none" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M 0 0 Q 10 -10 12 -20" fill="none" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M 0 0 Q 16 -6 22 -12" fill="none" strokeWidth="1.8" strokeLinecap="round" />
            <circle r="2" />
          </g>
          <rect x="-1.5" y="-10" width="3" height="14" fill="#e8c98b" stroke="none" />
          <g transform="translate(0 14)" strokeWidth="0.4">
            <path d="M -16 0 L 0 -3 L 16 0 L 16 8 L 0 5 L -16 8 Z" fill="#e8c98b" opacity="0.95" />
            <path d="M 0 -3 L 0 5" stroke="#2f5241" strokeWidth="0.9" />
          </g>
        </g>
      </g>
    </svg>
  );
};

export const IconPerseverance = () => {
  const { field, gold } = useRosetteIds();
  const FIELD = '#3d3345';
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor={FIELD} />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#c8a25c" accent="#e8c98b" />
        <g fill="#e8c98b" stroke="#e8c98b">
          <g transform="translate(0 -2)" strokeWidth="0.4">
            <path d="M -18 -8 Q 0 -14 18 -8 L 18 12 Q 0 6 -18 12 Z" opacity="0.95" />
            <path d="M 0 -12 L 0 8" stroke="#3d3345" strokeWidth="1.1" />
            <line x1="-12" y1="-4" x2="-4" y2="-4" stroke="#3d3345" strokeWidth="0.4" />
            <line x1="-12" y1="0" x2="-4" y2="0" stroke="#3d3345" strokeWidth="0.4" />
            <line x1="4" y1="-4" x2="12" y2="-4" stroke="#3d3345" strokeWidth="0.4" />
            <line x1="4" y1="0" x2="12" y2="0" stroke="#3d3345" strokeWidth="0.4" />
          </g>
          <ellipse cx="-22" cy="12" rx="6" ry="2" transform="rotate(-30 -22 12)" opacity="0.75" />
          <ellipse cx="22" cy="12" rx="6" ry="2" transform="rotate(30 22 12)" opacity="0.75" />
        </g>
      </g>
    </svg>
  );
};

export const IconResolve = () => {
  const { field, gold } = useRosetteIds();
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor="#1f3b3f" />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#c8a25c" accent="#e8c98b" />
        <g fill="none" stroke="#e8c98b" strokeWidth="1.5">
          <path d="M 0 -22 L 6 -6 L 22 -6 L 10 4 L 14 20 L 0 10 L -14 20 L -10 4 L -22 -6 L -6 -6 Z" fill="#e8c98b" opacity="0.15" />
          <path d="M 0 -22 L 6 -6 L 22 -6 L 10 4 L 14 20 L 0 10 L -14 20 L -10 4 L -22 -6 L -6 -6 Z" />
          <circle r="4" fill="#e8c98b" stroke="none" />
        </g>
      </g>
    </svg>
  );
};

export const IconYear = () => {
  const { field, gold } = useRosetteIds();
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor="#4a2727" />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#c8a25c" accent="#f4d38c" />
        <g fill="#f4d38c" stroke="#f4d38c">
          <rect x="-1.2" y="-30" width="2.4" height="10" />
          <rect x="-1.2" y="-30" width="2.4" height="10" transform="rotate(30)" />
          <rect x="-1.2" y="-30" width="2.4" height="10" transform="rotate(60)" />
          <rect x="-1.2" y="-30" width="2.4" height="10" transform="rotate(90)" />
          <rect x="-1.2" y="-30" width="2.4" height="10" transform="rotate(120)" />
          <rect x="-1.2" y="-30" width="2.4" height="10" transform="rotate(150)" />
          <rect x="-1.2" y="-30" width="2.4" height="10" transform="rotate(180)" />
          <rect x="-1.2" y="-30" width="2.4" height="10" transform="rotate(210)" />
          <rect x="-1.2" y="-30" width="2.4" height="10" transform="rotate(240)" />
          <rect x="-1.2" y="-30" width="2.4" height="10" transform="rotate(270)" />
          <rect x="-1.2" y="-30" width="2.4" height="10" transform="rotate(300)" />
          <rect x="-1.2" y="-30" width="2.4" height="10" transform="rotate(330)" />
          <circle r="14" fill="#f4d38c" opacity="0.9" />
          <circle r="14" fill="none" strokeWidth="1.4" stroke="#4a2727" />
          <circle r="5" fill="#4a2727" stroke="none" />
        </g>
      </g>
    </svg>
  );
};

export const IconBookArch = () => {
  const { field, gold } = useRosetteIds();
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor="#3a2a1a" />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#c8a25c" accent="#e8c98b" />
        <g fill="#e8c98b" stroke="#3a2a1a" strokeWidth="0.6">
          <rect x="-18" y="-2" width="36" height="8" rx="1" />
          <rect x="-14" y="-12" width="28" height="8" rx="1" opacity="0.9" />
          <rect x="-10" y="-22" width="20" height="8" rx="1" opacity="0.8" />
        </g>
      </g>
    </svg>
  );
};

export const IconLibrary = () => {
  const { field, gold } = useRosetteIds();
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor="#22343a" />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#c8a25c" accent="#e8c98b" />
        <g fill="#e8c98b" stroke="#22343a" strokeWidth="0.6">
          <rect x="-22" y="-14" width="6" height="26" />
          <rect x="-14" y="-18" width="6" height="30" />
          <rect x="-6" y="-12" width="6" height="24" />
          <rect x="2" y="-16" width="6" height="28" />
          <rect x="10" y="-10" width="6" height="22" />
          <rect x="18" y="-14" width="4" height="26" />
        </g>
      </g>
    </svg>
  );
};

export const IconMihrab = () => {
  const { field, gold } = useRosetteIds();
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor="#1e2b45" />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#c8a25c" accent="#e8c98b" />
        <g fill="#e8c98b">
          <path d="M -18 22 L -18 -4 Q 0 -22 18 -4 L 18 22 Z" opacity="0.95" />
          <path d="M -13 20 L -13 -2 Q 0 -18 13 -2 L 13 20 Z" fill="#1e2b45" />
          <g transform="translate(0 12)">
            <rect x="-8" y="-4" width="16" height="10" fill="#e8c98b" rx="0.8" />
            <line x1="0" y1="-4" x2="0" y2="6" stroke="#1e2b45" strokeWidth="0.8" />
          </g>
          <circle cx="0" cy="-8" r="1.6" />
        </g>
      </g>
    </svg>
  );
};

export const IconQuill = () => {
  const { field, gold } = useRosetteIds();
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor="#3a2a1a" />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#c8a25c" accent="#e8c98b" />
        <g fill="#e8c98b" stroke="#3a2a1a" strokeWidth="0.4">
          <path d="M -18 22 Q -4 4 8 -8 Q 22 -22 24 -18 Q 26 -2 8 10 Q -4 22 -18 22 Z" />
          <path d="M -18 22 L -24 28" stroke="#e8c98b" strokeWidth="2" strokeLinecap="round" />
          <circle cx="-6" cy="12" r="3" fill="#3a2a1a" stroke="none" />
        </g>
      </g>
    </svg>
  );
};

export const IconWaves = () => {
  const { field, gold } = useRosetteIds();
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor="#0f3f4a" />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#c8a25c" accent="#e8c98b" />
        <g fill="none" stroke="#e8c98b" strokeWidth="2" strokeLinecap="round">
          <path d="M -22 -8 Q -12 -18 -2 -8 T 22 -8" />
          <path d="M -22 4 Q -12 -6 -2 4 T 22 4" opacity="0.85" />
          <path d="M -22 16 Q -12 6 -2 16 T 22 16" opacity="0.7" />
        </g>
      </g>
    </svg>
  );
};

export const IconVariety = () => {
  const { field, gold } = useRosetteIds();
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor="#2f5241" />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#c8a25c" accent="#e8c98b" />
        <g fill="#e8c98b" stroke="#2f5241" strokeWidth="0.5">
          <g>
            <ellipse cx="0" cy="-14" rx="6" ry="14" opacity="0.95" />
            <path d="M 0 -28 L 0 0" stroke="#2f5241" strokeWidth="0.5" fill="none" />
          </g>
          <g transform="rotate(120)">
            <ellipse cx="0" cy="-14" rx="6" ry="14" opacity="0.95" />
            <path d="M 0 -28 L 0 0" stroke="#2f5241" strokeWidth="0.5" fill="none" />
          </g>
          <g transform="rotate(240)">
            <ellipse cx="0" cy="-14" rx="6" ry="14" opacity="0.95" />
            <path d="M 0 -28 L 0 0" stroke="#2f5241" strokeWidth="0.5" fill="none" />
          </g>
          <circle r="3" fill="#2f5241" stroke="none" />
        </g>
      </g>
    </svg>
  );
};

export const IconOlive = () => {
  const { field, gold } = useRosetteIds();
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor="#3d2544" />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#c8a25c" accent="#f2d59a" />
        <g fill="#e8c98b" stroke="#3d2544" strokeWidth="0.4">
          <path d="M 0 24 L 0 -18" stroke="#e8c98b" strokeWidth="1.6" fill="none" strokeLinecap="round" />
          <ellipse cx="-14" cy="18"  rx="8" ry="3" transform="rotate(-30 -14 18)" />
          <ellipse cx="12"  cy="12"  rx="8" ry="3" transform="rotate(25 12 12)" />
          <ellipse cx="-16" cy="4"   rx="8" ry="3" transform="rotate(-30 -16 4)" />
          <ellipse cx="14"  cy="-2"  rx="8" ry="3" transform="rotate(25 14 -2)" />
          <ellipse cx="-14" cy="-12" rx="8" ry="3" transform="rotate(-30 -14 -12)" />
          <ellipse cx="12"  cy="-18" rx="8" ry="3" transform="rotate(25 12 -18)" />
          <circle cx="-10" cy="12" r="1.8" fill="#8a5a2d" stroke="none" />
          <circle cx="8"   cy="6"  r="1.8" fill="#8a5a2d" stroke="none" />
          <circle cx="-9"  cy="-4" r="1.8" fill="#8a5a2d" stroke="none" />
        </g>
      </g>
    </svg>
  );
};

export const IconPeak = () => {
  const { field, gold } = useRosetteIds();
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <FrameDefs fieldId={field} goldId={gold} fieldColor="#3a2a1a" />
      <g transform="translate(60 60)">
        <FrameStar goldId={gold} />
        <FrameField fieldId={field} ring="#e8c98b" accent="#f4d38c" />
        <g fill="#f4d38c">
          <g opacity="0.35">
            <rect x="-1" y="-34" width="2" height="10" />
            <rect x="-1" y="-34" width="2" height="10" transform="rotate(45)" />
            <rect x="-1" y="-34" width="2" height="10" transform="rotate(90)" />
            <rect x="-1" y="-34" width="2" height="10" transform="rotate(135)" />
            <rect x="-1" y="-34" width="2" height="10" transform="rotate(180)" />
            <rect x="-1" y="-34" width="2" height="10" transform="rotate(225)" />
            <rect x="-1" y="-34" width="2" height="10" transform="rotate(270)" />
            <rect x="-1" y="-34" width="2" height="10" transform="rotate(315)" />
          </g>
          <g transform="translate(0 6)">
            <path d="M -18 8 L -18 -6 L -12 -6 L -12 -12 L -6 -12 L -6 -6 L 6 -6 L 6 -12 L 12 -12 L 12 -6 L 18 -6 L 18 8 Z" />
            <rect x="-3" y="-2" width="6" height="10" fill="#3a2a1a" />
            <circle cx="0" cy="0" r="1.2" fill="#f4d38c" />
          </g>
          <path d="M 0 -20 L 2.5 -15 L 8 -14 L 4 -10 L 5 -5 L 0 -8 L -5 -5 L -4 -10 L -8 -14 L -2.5 -15 Z" />
        </g>
      </g>
    </svg>
  );
};

const REGISTRY = {
  steadfast: IconSteadfast,
  perseverance: IconPerseverance,
  resolve: IconResolve,
  year: IconYear,
  book_arch: IconBookArch,
  library: IconLibrary,
  mihrab: IconMihrab,
  quill: IconQuill,
  waves: IconWaves,
  variety: IconVariety,
  peak: IconPeak,
  olive: IconOlive,
};

export const BadgeIcon = ({ name }) => {
  const Cmp = REGISTRY[name] || IconBookArch;
  return <Cmp />;
};

export default BadgeIcon;
